import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  useGetAccountTypesQuery,
  useDeleteAccountTypeMutation,
  useToggleAccountTypeStatusMutation,
} from '../../../store/api/adminApi';
import PageLoader from '../../../components/PageLoader';
import ActionButtons from '../../../components/ActionButtons';
import Pagination from '../../../components/Pagination';
import { showSuccess, showError } from '../../../utils/toast';

const formatDate = (d: string | null | undefined) => {
  if (!d) return '-';
  const dt = new Date(d);
  return `${dt.getDate().toString().padStart(2, '0')} ${['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][dt.getMonth()]}, ${dt.getFullYear()}`;
};

const AdminAccountTypes: React.FC = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const { data, isLoading, error, refetch } = useGetAccountTypesQuery({ search, page });
  const [deleteAccountType] = useDeleteAccountTypeMutation();
  const [toggleAccountTypeStatus] = useToggleAccountTypeStatusMutation();

  useEffect(() => { setPage(1); }, [search]);

  const handleToggleStatus = async (id: number, current: boolean) => {
    try {
      const result = await toggleAccountTypeStatus({ id, status: !current }).unwrap();
      showSuccess(result.message || 'Status updated!');
      refetch();
    } catch (err: any) {
      showError(err?.data?.message || 'Failed to update status.');
    }
  };

  const handleDelete = async (id: number) => {
    try {
      const result = await deleteAccountType(id).unwrap();
      showSuccess(result.message || 'Account type deleted!');
      refetch();
    } catch (err: any) {
      showError(err?.data?.message || 'Failed to delete account type.');
    }
  };

  if (isLoading) return <PageLoader />;
  if (error) return <div className="alert alert-danger m-5">Error loading account types</div>;

  const types = data?.data || { data: [], current_page: 1, last_page: 1, total: 0, per_page: 15 };

  return (
    <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
      <div className="d-flex flex-column flex-column-fluid">

        <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
          <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
            <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
              <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">Account Types</h1>
              <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                <li className="breadcrumb-item text-muted">
                  <Link to="/admin" className="text-muted text-hover-primary">Home</Link>
                </li>
                <li className="breadcrumb-item"><span className="bullet bg-gray-400 w-5px h-2px"></span></li>
                <li className="breadcrumb-item text-muted">Account Types</li>
              </ul>
            </div>
          </div>
        </div>

        <div id="kt_app_content" className="app-content flex-column-fluid">
          <div id="kt_app_content_container" className="app-container container-xxl">
            <div className="card">

              <div className="card-header border-0 pt-6">
                <div className="card-title">
                  <div className="d-flex align-items-center position-relative my-1">
                    <span className="svg-icon svg-icon-1 position-absolute ms-6">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <rect opacity="0.5" x="17.0365" y="15.1223" width="8.15546" height="2" rx="1" transform="rotate(45 17.0365 15.1223)" fill="currentColor"/>
                        <path d="M11 19C6.55556 19 3 15.4444 3 11C3 6.55556 6.55556 3 11 3C15.4444 3 19 6.55556 19 11C19 15.4444 15.4444 19 11 19ZM11 5C7.53333 5 5 7.53333 5 11C5 14.4667 7.53333 17 11 17C14.4667 17 17 14.4667 17 11C17 7.53333 14.4667 5 11 5Z" fill="currentColor"/>
                      </svg>
                    </span>
                    <input
                      type="text"
                      className="form-control form-control-solid w-300px ps-15"
                      placeholder="Search account type..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                    {search && (
                      <a href="#" onClick={(e) => { e.preventDefault(); setSearch(''); }} className="btn btn-sm btn-light-primary ms-3">Clear</a>
                    )}
                  </div>
                </div>
                <div className="card-toolbar">
                  <button className="btn btn-base" onClick={() => navigate('/admin/account-types/add')}>
                    <span className="svg-icon svg-icon-2">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <rect opacity="0.5" x="11.364" y="20.364" width="16" height="2" rx="1" transform="rotate(-90 11.364 20.364)" fill="currentColor"/>
                        <rect x="4.36396" y="11.364" width="16" height="2" rx="1" fill="currentColor"/>
                      </svg>
                    </span>
                    Add Account Type
                  </button>
                </div>
              </div>

              <div className="card-body pt-0">
                <div className="table-responsive">
                  <table className="table align-middle table-row-dashed fs-6 gy-5">
                    <thead>
                      <tr className="text-start text-gray-400 fw-bold fs-7 text-uppercase gs-0">
                        <th>#</th>
                        <th>Title</th>
                        <th>Title (Arabic)</th>
                        <th>Status</th>
                        <th>Created</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody className="fw-semibold text-gray-600">
                      {types.data.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="text-center py-10">
                            <span className="text-muted">No account types found</span>
                          </td>
                        </tr>
                      ) : (
                        types.data.map((type: any, i: number) => (
                          <tr key={type.id}>
                            <td>{(types.current_page - 1) * types.per_page + i + 1}</td>
                            <td>{type.title || '-'}</td>
                            <td>{type.title_ar || '-'}</td>
                            <td>
                              <div className="form-check form-switch">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  checked={type.status === true || type.status === 1}
                                  onChange={() => handleToggleStatus(type.id, !!type.status)}
                                  style={{ cursor: 'pointer' }}
                                />
                              </div>
                            </td>
                            <td>{formatDate(type.created_at)}</td>
                            <td>
                              <ActionButtons
                                onEdit={() => navigate(`/admin/account-types/${type.id}/edit`)}
                                onDelete={() => handleDelete(type.id)}
                              />
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {types.data.length > 0 && (
                  <div className="card-footer border-0 pt-6">
                    <Pagination
                      currentPage={types.current_page}
                      lastPage={types.last_page}
                      total={types.total || 0}
                      perPage={types.per_page || 15}
                      onPageChange={(p) => setPage(p)}
                      showTotal={true}
                    />
                  </div>
                )}
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAccountTypes;
